'use client'
import { CheckCircle, Lock, Terminal } from 'lucide-react'
import React, { useState } from 'react'
import { motion } from 'framer-motion'

const ContactSection = () => {
	const [currentStep, setCurrentStep] = useState(1)
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		message: '',
		projectType: 'personal',
	})
	const [errors, setErrors] = useState<any>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitSuccess, setSubmitSuccess] = useState(false)

	const floatingVariants = {
		float: {
			y: [0, -20, 0],
			rotate: [0, -5, 5, 0],
			transition: {
				duration: 8,
				repeat: Infinity,
				ease: [0.42, 0, 0.58, 1] as const,
			},
		},
	}

	const validate = (step: any) => {
		const tempErrors:any = {}
		if (step === 1) {
			if (!formData.name.trim()) {
				tempErrors.name = 'Name is required'
			}
			if (!formData.email.trim()) {
				tempErrors.email = 'Email is required'
			} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
				tempErrors.email = 'Email is invalid'
			}
		} else if (step === 2) {
			if (!formData.message.trim()) {
				tempErrors.message = 'Message is required'
			}
		}
		setErrors(tempErrors)
		return Object.keys(tempErrors).length === 0
	}

	const handleNext = () => {
		if (validate(currentStep)) {
			if (currentStep < 2) {
				setCurrentStep((prev) => prev + 1)
			} else {
				handleSubmit()
			}
		}
	}

	const handleSubmit = async () => {
		if (validate(2)) {
			// Final validation before submitting
			setIsSubmitting(true)
			try {
				const response = await fetch('https://formspree.io/f/mjkgeeop', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(formData),
				})

				if (response.ok) {
					setSubmitSuccess(true)
					setCurrentStep(3)
				} else {
					const data = await response.json()
					if (data.errors) {
						//Formspree specific error handling
						const formspreeErrors:any = {}
						data.errors.forEach((error: any) => {
							formspreeErrors[error.field] = error.message // Assuming Formspree gives field and message
						})

						//Splice formspree error message
						Object.keys(formspreeErrors).forEach((key:any) => {
							formspreeErrors[key] = formspreeErrors[key].split(' ').slice(2).join(' ')
						})

						setErrors(formspreeErrors)
					} else {
						// Handle generic errors
						setErrors({ form: 'An unexpected error occurred.  Please try again.' })
					}
				}
			} catch (error) {
				setErrors({ form: 'An unexpected error occurred.  Please try again.' })
				console.error('Error submitting form:', error)
			} finally {
				setIsSubmitting(false)
			}
		}
	}

	const handleResetForm = () => {
		setCurrentStep(1)
		setFormData({
			name: '',
			email: '',
			message: '',
			projectType: 'personal',
		})
		setErrors({})
		setSubmitSuccess(false)
	}

	return (
		<section id="contact" className="relative py-12 bg-gradient-to-b to-[#f6f6f6] from-white">
			<div className="max-w-6xl mx-auto px-4">
				<motion.div
					initial={{ opacity: 0, y: 50 }}
					whileInView={{ opacity: 1, y: 0 }}
					className="relative bg-white rounded-[2.5rem] shadow-lg overflow-hidden border border-white/20"
				>
					{/* Animated Background Elements */}
					<div className="absolute inset-0 overflow-hidden opacity-20">
						<motion.div
							className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] rounded-full blur-3xl opacity-10"
							variants={floatingVariants}
							animate="float"
						/>
						<motion.div
							className="absolute top-1/2 right-1/4 w-48 h-48 bg-gradient-to-br from-[#3BCF8E] to-[#2DA771] rounded-full blur-2xl opacity-10"
							variants={floatingVariants}
							animate="float"
						/>
					</div>

					<div className="relative z-10 grid lg:grid-cols-2 gap-12 p-12">
						{/* Interactive Preview Side */}
						<div className="hidden lg:block relative">
							<div className="sticky top-24 space-y-8">
								<Terminal className="h-16 w-16 text-[#3BCF8E] mb-8" />
								<h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
									Let&apos;s Build
									<span className="block text-[#3BCF8E] mt-2">Something Great</span>
								</h3>
							</div>
						</div>

						{/* Stepped Form */}
						<div className="space-y-8">
							<div className="flex items-center justify-between mb-8">
								<div className="flex space-x-2">
									{[1, 2, 3].map((step) => (
										<div
											key={step}
											className={`h-2 w-8 rounded-full transition-all ${
												currentStep >= step ? 'bg-[#3BCF8E]' : 'bg-gray-200'
											}`}
										/>
									))}
								</div>
								<div className="flex items-center space-x-2 text-gray-500">
									<Lock className="h-4 w-4" />
									<span className="text-sm">Secure Connection</span>
								</div>
							</div>

							<form onSubmit={(e) => e.preventDefault()}>
								{currentStep === 1 && (
									<motion.div
										initial={{ opacity: 0, x: 20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: -20 }}
										className="space-y-6"
									>
										<h4 className="text-2xl font-bold text-gray-900">Contact Information</h4>
										<div className="space-y-4">
											<div>
												<label htmlFor="contact-name" className="block text-gray-700 mb-2">Full Name</label>
												<input
													id="contact-name"
													type="text"
													aria-label="Full Name"
													className={`w-full px-4 py-3 rounded-xl border bg-white transition-all ${
														errors.name
															? 'border-red-500'
															: 'border-gray-200 focus-visible:border-[#3BCF8E] focus-visible:ring-2 focus-visible:ring-[#3BCF8E]/20 focus-visible:outline-none'
													}`}
													value={formData.name}
													onChange={(e) => setFormData({ ...formData, name: e.target.value })}
												/>
												{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
											</div>
											<div>
												<label htmlFor="contact-email" className="block text-gray-700 mb-2">Work Email</label>
												<input
													id="contact-email"
													type="email"
													aria-label="Work Email"
													className={`w-full px-4 py-3 rounded-xl border bg-white transition-all ${
														errors.email
															? 'border-red-500'
															: 'border-gray-200 focus-visible:border-[#3BCF8E] focus-visible:ring-2 focus-visible:ring-[#3BCF8E]/20 focus-visible:outline-none'
													}`}
													value={formData.email}
													onChange={(e) => setFormData({ ...formData, email: e.target.value })}
												/>
												{errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
											</div>
										</div>
									</motion.div>
								)}

								{currentStep === 2 && (
									<motion.div
										initial={{ opacity: 0, x: 20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: -20 }}
										className="space-y-6"
									>
										<h4 className="text-2xl font-bold text-gray-900">Project Details</h4>
										<div className="space-y-4">
											<div>
												<label className="block text-gray-700 mb-2">Project Type</label>
												<div className="grid grid-cols-2 gap-4">
													{['Personal', 'Team', 'Enterprise', 'Other'].map((type) => (
														<button
															key={type}
															type="button"
															onClick={() =>
																setFormData({
																	...formData,
																	projectType: type.toLowerCase(),
																})
															}
															className={`p-4 rounded-xl border transition-all ${
																formData.projectType === type.toLowerCase()
																	? 'border-[#3BCF8E] bg-[#3BCF8E]/10'
																	: 'border-gray-200 hover:border-[#3BCF8E]/40'
															}`}
														>
															{type}
														</button>
													))}
												</div>
											</div>
											<div>
												<label htmlFor="contact-message" className="block text-gray-700 mb-2">Message</label>
												<textarea
													id="contact-message"
													rows={4}
													aria-label="Message"
													className={`w-full px-4 py-3 rounded-xl border bg-white transition-all ${
														errors.message
															? 'border-red-500'
															: 'border-gray-200 focus-visible:border-[#3BCF8E] focus-visible:ring-2 focus-visible:ring-[#3BCF8E]/20 focus-visible:outline-none'
													}`}
													value={formData.message}
													onChange={(e) => setFormData({ ...formData, message: e.target.value })}
												/>
												{errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
											</div>
										</div>
									</motion.div>
								)}
							</form>

							{currentStep === 3 && (
								<motion.div
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									className="text-center py-12"
								>
									<motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-block mb-6">
										<CheckCircle className="h-16 w-16 text-[#3BCF8E]" />
									</motion.div>
									<h4 className="text-2xl font-bold text-gray-900 mb-4">Message Sent!</h4>
									<p className="text-gray-600">
										We&apos;ll get back to you within 24 hours. Check your email for confirmation.
									</p>
								</motion.div>
							)}
							{errors.form && <p className="text-red-500 text-sm mt-1 text-center">{errors.form}</p>}
							<div className="pt-8">
								{currentStep < 3 ? (
									<motion.button
										type="button"
										whileHover={{ scale: 1.02 }}
										whileTap={{ scale: 0.98 }}
										className="w-full bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white py-4 rounded-xl font-semibold shadow-lg relative overflow-hidden"
										onClick={handleNext}
										disabled={isSubmitting}
									>
										<span className="relative z-10">{currentStep === 1 ? 'Continue' : 'Send Message'}</span>
										<div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-20 transition-opacity" />
									</motion.button>
								) : (
									<button
										type="button"
										className="w-full py-4 rounded-xl font-semibold border border-gray-200 hover:border-[#3BCF8E]/40 text-gray-600 hover:text-[#3BCF8E] transition-colors"
										onClick={handleResetForm}
									>
										Send Another Message
									</button>
								)}
							</div>
						</div>
					</div>
				</motion.div>
			</div>
		</section>
	)
}

export default ContactSection